import React from "react"
import { Box, IconButton, Typography } from "@mui/material"
import { DrawerSeeker, IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"


const BRAND_GRADIENT = `linear-gradient(135deg, ${dsColorNew.webBlue500}, ${dsColorNew.cyan})`

// Head of a form: icon tile, title and a line about it. A full-page form goes back with an arrow on the left,
// a side panel closes with an × on the right; both call the same close the form already had.
export function AppFormDrawerHeader({ icon = 'pen', title, subtitle, onClose, fullPage = false, aside }) {
	const closeButton = !!onClose && <IconButton
		aria-label={fullPage ? 'Back' : 'Close'}
		onClick={onClose}
		sx={fullPage
			? { width: 34, height: 34, borderRadius: '8px', border: `1px solid ${dsColorNew.grayBorder}`, color: dsColorNew.textMuted }
			: { width: 32, height: 32, borderRadius: '8px', color: dsColorNew.textSubtle }}
	>
		<IconSeeker icon={fullPage ? 'arrow-left' : 'xmark'} sx={{ fontSize: fullPage ? 14 : 15 }}/>
	</IconButton>

	return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5 }}>
		{fullPage && closeButton}
		<Box sx={{
			width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
			display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
			background: BRAND_GRADIENT, color: dsColorNew.white,
		}}>
			<IconSeeker icon={icon} sx={{ fontSize: 16 }}/>
		</Box>
		<Box sx={{ minWidth: 0 }}>
			<Typography component="h2" sx={{ fontSize: 17, fontWeight: 600, color: dsColorNew.textTitle, lineHeight: 1.3 }}>{title}</Typography>
			{!!subtitle && <Typography component="div" sx={{ fontSize: 13, color: dsColorNew.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</Typography>}
		</Box>
		<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
			{aside}
			{!fullPage && closeButton}
		</Box>
	</Box>
}

// A form in a drawer with the app's chrome: the header always in view, the body on the page grey with its
// sections as cards, and the actions at the foot. `width="100%"` makes it a page over the screen with the
// body in a centred column; any other width is a side panel. Open / close / footer work as in DrawerSeeker.
export default function AppFormDrawer({
	isOpen, controller, onClose, title, subtitle, icon, headerAside, footer, width = 520, children,
	contentSx = {}, ...props
}) {
	const fullPage = width === '100%'
	const close = onClose || controller

	return <DrawerSeeker
		isOpen={isOpen}
		controller={controller}
		anchor="right"
		width={width}
		hideActions={!footer}
		customFooter={footer}
		{...props}
		title={<AppFormDrawerHeader icon={icon} title={title} subtitle={subtitle} onClose={close} fullPage={fullPage} aside={headerAside}/>}
		titleProps={{ sx: { p: 0, borderBottom: `1px solid ${dsColorNew.grayBorder}`, backgroundColor: dsColorNew.white } }}
		contentProps={{ dividers: false, sx: { backgroundColor: dsColorNew.contentBg, px: 3, py: 2.5, '.MuiDialogTitle-root + &': { pt: 2.5 } } }}
		actionProps={{ sx: { px: 3, py: 1.5, gap: 1, borderTop: `1px solid ${dsColorNew.grayBorder}`, backgroundColor: dsColorNew.white } }}
	>
		<Box sx={{ ...(fullPage && { maxWidth: 1080, mx: 'auto' }), display: 'flex', flexDirection: 'column', gap: 2, ...contentSx }}>
			{children}
		</Box>
	</DrawerSeeker>
}
